/**
 * SF-4845: Copy environment (copy production onto stage or dev, or stage and dev onto each other).
 * sf-api runs the copy and checks every rule again; these mirror its contract
 * (services/envCopy/environments.js and steps.js).
 */

/** Permission for every /admin/env-copy route on sf-api, the menu entry and the page here. */
export const ENV_COPY_PERMISSION = 'eng:env-copy';

/** Refine resource for the menu (App.tsx and the access test share it). */
export const ENV_COPY_RESOURCE = {
  name: 'env-copy',
  list: '/eng/env-copy',
  show: '/eng/env-copy/runs/:id',
} as const;

export const ENV_COPY_PATH = '/eng/env-copy';
export const envCopyRunPath = (runId: string) =>
  `${ENV_COPY_PATH}/runs/${encodeURIComponent(runId)}`;

export type EnvName = 'prod' | 'stage' | 'dev';
export const SOURCES: readonly EnvName[] = ['prod', 'stage', 'dev'];
/** Production is never a target, whatever the server sends. */
export const COPY_TARGETS: readonly EnvName[] = ['stage', 'dev'];

export const ENV_LABELS: Record<string, string> = {
  prod: 'Production',
  stage: 'Stage',
  dev: 'Dev',
};
export const envLabel = (env: string | null | undefined) =>
  (env && ENV_LABELS[env]) || env || 'Unknown';

/** How often a queued or running copy is read again. */
export const ENV_COPY_POLL_MS = 3000;
/** A run read that gets no answer in this time fails, so the next read can go out. */
export const ENV_COPY_READ_TIMEOUT_MS = 15_000;
/**
 * After a resume is accepted, how long the page waits for the runner to pick the copy up. With no
 * sign of it by then the page stops reading and says the copy has not started yet.
 */
export const ENV_COPY_RESUME_WAIT_MS = 3 * 60_000;
/** SF-5084: the month limit for images and report files the form starts with. */
export const FILES_MONTHS_DEFAULT = 1;
/** How many recent copies the list shows. */
export const ENV_COPY_LIST_LIMIT = 20;

export type RunStatus = 'queued' | 'running' | 'done' | 'failed' | 'recovery_required';
export type StepState = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

export const RUN_STATUS_LABELS: Record<RunStatus, { label: string; color: string }> = {
  queued: { label: 'Waiting to start', color: 'default' },
  running: { label: 'Running', color: 'processing' },
  done: { label: 'Done', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  recovery_required: { label: 'Needs resuming', color: 'warning' },
};

export const STEP_STATE_LABELS: Record<StepState, { label: string; color: string }> = {
  pending: { label: 'Waiting', color: 'default' },
  running: { label: 'Running', color: 'processing' },
  done: { label: 'Done', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  skipped: { label: 'Skipped', color: 'default' },
};

/** A run is still changing while queued or running; only then is it read again. */
export const isLiveStatus = (status: string | null | undefined) =>
  status === 'queued' || status === 'running';

/** Same match as sf-api validateCopyRequest: trimmed and case-insensitive. */
export const confirmMatches = (typed: string, target: string) =>
  typed.trim().toLowerCase() === target.toLowerCase();
